import { useAtom } from 'jotai';
import { useEffect, useMemo, useState } from 'react';
import type { TextInput } from 'react-native';
import { useClickOutside } from 'react-native-click-outside';
import {
    BodyMediumEmphasized,
    BodyMediumRegular,
    ChevronRightIcon,
    Icon,
    isWeb,
    Private,
    StyledBodyMediumEmphasizedInput,
    XStack,
} from 'smoothie';

import { isEmpty } from '../../../common/misc';
import { currentlyEditingBillFieldAtom } from '../../state/BillsAtoms';
import type { InlineEditableBillFields } from '../../types/Bill';

interface BillDetailsRowProps {
    label: string;
    value: string;
    formattedValue?: string;
    placeholder?: string;
    type?: 'string' | 'number';
    onPress?: () => void;
    onChange?: (oldValue: string, newValue: string) => string;
    onSubmit?: (value: string | number) => void;
    showCaret?: boolean;
    hasProposedChange?: boolean;
    autoFocus?: boolean;
}

const BillDetailsRow = ({
    label,
    value,
    formattedValue,
    placeholder,
    type = 'string',
    onPress,
    onChange,
    onSubmit,
    showCaret,
    hasProposedChange,
    autoFocus,
}: BillDetailsRowProps) => {
    const [currentlyEditingBillField, setCurrentlyEditingBillField] = useAtom(
        currentlyEditingBillFieldAtom,
    );

    const [newValue, setNewValue] = useState(value);
    const showPlaceholder =
        isEmpty(newValue) || (type === 'number' && newValue === '$0.00');
    const TextComponent = showPlaceholder
        ? BodyMediumRegular
        : BodyMediumEmphasized;

    const isBeingEdited = useMemo(
        () =>
            (currentlyEditingBillField ?? '') ===
            (label as InlineEditableBillFields),
        [currentlyEditingBillField],
    );

    useEffect(() => {
        setNewValue(value);
    }, [value]);

    useEffect(() => {
        let handler: (event: MouseEvent) => void;

        if (isWeb && isBeingEdited) {
            handler = (event: MouseEvent) => {
                const { id } = event.target as HTMLElement;
                if (newValue !== value && onSubmit && id !== label) {
                    // Trim and convert empty strings to empty string for optional fields
                    const trimmedValue = (newValue ?? '').trim();
                    onSubmit(trimmedValue);
                    document.removeEventListener('click', handler);
                }
            };
            document.addEventListener('click', handler);
        }

        if (isWeb) {
            return () => {
                document.removeEventListener('click', handler);
            };
        }
        return () => {};
        // isBeingEdited is not listed as a dependency since the click handler is called after the value has been updated
    }, [newValue]);

    const shouldAutoFocus = autoFocus && isBeingEdited && !hasProposedChange;

    const ref = useClickOutside<TextInput>(() => {
        // Mobile-specific behavior
        if (newValue !== value && onSubmit) {
            // Trim and validate before submitting
            const trimmedValue = (newValue ?? '').trim();
            onSubmit(trimmedValue);
        }
        setCurrentlyEditingBillField(null);
    });

    return (
        <XStack
            paddingVertical={16}
            alignItems="flex-start"
            justifyContent="space-between"
            onPress={onPress}
        >
            <BodyMediumRegular color="$black">{label}</BodyMediumRegular>
            <XStack gap={4} alignItems="center" flexShrink={1}>
                {!showCaret && isBeingEdited && !hasProposedChange ? (
                    <Private>
                        <StyledBodyMediumEmphasizedInput
                            ref={ref}
                            id={label}
                            padding={0}
                            value={newValue}
                            textAlign="right"
                            height={22}
                            onChangeText={(incomingValue) => {
                                const formattedValue = onChange
                                    ? onChange(newValue, incomingValue)
                                    : incomingValue;
                                setNewValue(formattedValue);
                            }}
                            onSubmitEditing={() => {
                                if (onSubmit) onSubmit(newValue ?? '');
                            }}
                            enterKeyHint="done"
                            // eslint-disable-next-line jsx-a11y/no-autofocus
                            autoFocus={shouldAutoFocus}
                            keyboardType={
                                type === 'number' ? 'decimal-pad' : 'default'
                            }
                            placeholder={placeholder ?? ''}
                        />
                    </Private>
                ) : (
                    <XStack gap={8} alignItems="flex-start">
                        <TextComponent
                            flexShrink={1}
                            id={label}
                            numberOfLines={2}
                            textAlign="right"
                            color={
                                hasProposedChange
                                    ? '$orange1000'
                                    : showPlaceholder
                                      ? '$gray1000'
                                      : '$black'
                            }
                        >
                            {showPlaceholder
                                ? placeholder
                                : (formattedValue ?? value)}
                        </TextComponent>
                        {showCaret && (
                            <Icon
                                icon={ChevronRightIcon}
                                color="$gray1000"
                                style={{ marginTop: 3 }}
                            />
                        )}
                    </XStack>
                )}
            </XStack>
        </XStack>
    );
};

export default BillDetailsRow;
